import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { supportMessages } from './supportMessages';
import { Panel, PanelBody, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import styles from './support.module.scss';
/* eslint-enable css-modules/no-unused-class */

const COPIED_VISIBLE_MS = 2000;

interface Props {
  userEmail: string;
  workspace: string | null;
  appVersion: string;
  deviceId: string | undefined;
}

/** "Details we attach": what a message carries, and a copy button (design settings-redesign d1). */
const SupportDetailsCardComponent: React.FC<Props> = ({ userEmail, workspace, appVersion, deviceId }) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = setTimeout(() => setCopied(false), COPIED_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const rows = useMemo<[string, string][]>(
    () => [
      [intl.formatMessage(supportMessages.signedInAs), userEmail],
      [intl.formatMessage(supportMessages.workspace), workspace ?? ''],
      [intl.formatMessage(supportMessages.appVersion), appVersion],
      [intl.formatMessage(supportMessages.deviceId), deviceId ?? ''],
    ],
    [appVersion, deviceId, intl, userEmail, workspace]
  );

  const copy = useCallback(() => {
    const text = rows.map(([label, value]) => `${label}: ${value}`).join('\n');
    navigator.clipboard
      .writeText(text)
      .then(() => setCopied(true))
      .catch(console.error);
  }, [rows]);

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(supportMessages.detailsTitle)}
        subtitle={intl.formatMessage(supportMessages.detailsDescription)}
      />
      <PanelBody stack>
        <dl className={styles.details} data-clarity-mask="true">
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd title={value}>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
        <div>
          <Button color="secondary" outline size="sm" onClick={copy}>
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-2" />
            {intl.formatMessage(copied ? supportMessages.copied : supportMessages.copy)}
          </Button>
        </div>
      </PanelBody>
    </Panel>
  );
};

export const SupportDetailsCard = TypedMemo(SupportDetailsCardComponent);
